import { useState, useEffect, useCallback } from 'react';
import { getCategories, createCategory, deleteCategory, updateCategory, getItems, createItem, updateItem, toggleItem, deleteItem, uploadImage } from '../api';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableCategory({ cat, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cat.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={{ ...itemStyles.tag, ...style }}>
      <span {...attributes} {...listeners} style={itemStyles.dragHandle}>⠿</span>
      <span style={itemStyles.tagName}>{cat.name}</span>
      <button style={itemStyles.tagEdit} onClick={() => onEdit(cat)}>Edit</button>
      <button style={itemStyles.tagDelete} onClick={() => onDelete(cat.id)}>x</button>
    </div>
  );
}

function SortableItem({ item, onToggle, onEdit, onDuplicate, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={{ ...itemStyles.itemCard, ...style, opacity: isDragging ? 0.5 : item.is_available ? 1 : 0.6 }}>
      <span {...attributes} {...listeners} style={itemStyles.dragHandle}>⠿</span>
      <div style={itemStyles.itemLeft}>
        {item.image_url && <img src={item.image_url && item.image_url.startsWith('http') ? item.image_url : (process.env.REACT_APP_API_URL || '') + item.image_url} alt={item.name} style={itemStyles.itemImage} />}
      </div>
      <div style={itemStyles.itemInfo}>
        <p style={itemStyles.itemName}>{item.name}</p>
        {item.description && <p style={itemStyles.itemDesc}>{item.description}</p>}
        <p style={itemStyles.itemPrice}>N{Number(item.price).toLocaleString()}</p>
        {item.dietary_labels?.length > 0 && (
          <div style={itemStyles.labelRow}>
            {item.dietary_labels.map(l => (<span key={l} style={itemStyles.labelBadge}>{l}</span>))}
          </div>
        )}
      </div>
      <div style={itemStyles.itemActions}>
        <button style={item.is_available ? itemStyles.availableBtn : itemStyles.unavailableBtn} onClick={() => onToggle(item)}>
          {item.is_available ? 'Available' : 'Sold Out'}
        </button>
        <button style={itemStyles.editBtn} onClick={() => onEdit(item)}>Edit</button>
        <button style={itemStyles.dupBtn} onClick={() => onDuplicate(item)}>Copy</button>
        <button style={itemStyles.deleteButton} onClick={() => onDelete(item.id)}>Delete</button>
      </div>
    </div>
  );
}

function SortableCategoryItems({ categoryId, items, sensors, onDragEnd, onToggle, onEdit, onDuplicate, onDelete }) {
  const catItems = items.filter(i => i.category_id === categoryId);
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => onDragEnd(e, catItems)}>
      <SortableContext items={catItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
        {catItems.map(item => (
          <SortableItem
            key={item.id}
            item={item}
            onToggle={onToggle}
            onEdit={onEdit}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}

const DIETARY_OPTIONS = ['Vegan', 'Vegetarian', 'Gluten-Free', 'Nut-Free', 'Dairy-Free', 'Halal', 'Kosher'];
const emptyItem = { name: '', description: '', price: '', category_id: '', dietary_labels: [], allergen_info: '' };

function MenuDetail({ menu, onBack }) {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [newCatName, setNewCatName] = useState('');
  const [editingCat, setEditingCat] = useState(null);
  const [editingCatName, setEditingCatName] = useState('');
  const [newItem, setNewItem] = useState(emptyItem);
  const [editingItem, setEditingItem] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchAll = useCallback(async () => {
    try {
      const [catRes, itemRes] = await Promise.all([getCategories(menu.id), getItems(menu.id)]);
      setCategories(catRes.data);
      setItems(itemRes.data);
    } catch (err) {
      setError('Failed to load menu data');
    } finally {
      setLoading(false);
    }
  }, [menu.id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCatName.trim()) return;
    try {
      const res = await createCategory({ menu_id: menu.id, name: newCatName });
      setCategories([...categories, res.data]);
      setNewCatName('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create category');
    }
  };

  const handleRenameCategory = async () => {
    if (!editingCatName.trim()) return;
    try {
      const res = await updateCategory(editingCat.id, { name: editingCatName });
      setCategories(categories.map(c => c.id === editingCat.id ? { ...c, name: res.data.name } : c));
      setEditingCat(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to rename category');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await deleteCategory(id);
      setCategories(categories.filter(c => c.id !== id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete category');
    }
  };

  const handleCategoryDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = categories.findIndex(c => c.id === active.id);
    const newIndex = categories.findIndex(c => c.id === over.id);
    const reordered = arrayMove(categories, oldIndex, newIndex);
    setCategories(reordered);
    try {
      await Promise.all(reordered.map((cat, i) => updateCategory(cat.id, { position: i + 1 })));
    } catch (err) {
      setError('Failed to save category order');
      fetchAll();
    }
  };

  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError('Image must be under 10MB'); return; }
    const preview = URL.createObjectURL(file);
    if (type === 'new') { setImageFile(file); setImagePreview(preview); }
    else { setEditImageFile(file); setEditImagePreview(preview); }
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();
    if (!newItem.name || !newItem.price || !newItem.category_id) {
      setError('Item name, price and category are required');
      return;
    }
    try {
      setUploading(true);
      setError('');
      let imageUrl = null;
      if (imageFile) {
        const formData = new FormData();
        formData.append('image', imageFile);
        const uploadRes = await uploadImage(formData);
        imageUrl = uploadRes.data.url;
      }
      const { image_url: _ignored, ...itemData } = newItem;
      const res = await createItem({ ...itemData, menu_id: menu.id, price: parseFloat(newItem.price), image_url: imageUrl });
      setItems([...items, res.data]);
      setNewItem(emptyItem);
      setImageFile(null);
      setImagePreview(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create item');
    } finally {
      setUploading(false);
    }
  };

  const handleEditItem = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      setError('');
      let imageUrl = editingItem.image_url;
      if (editImageFile) {
        const formData = new FormData();
        formData.append('image', editImageFile);
        const uploadRes = await uploadImage(formData);
        imageUrl = uploadRes.data.url;
      }
      const res = await updateItem(editingItem.id, {
        name: editingItem.name,
        description: editingItem.description,
        price: parseFloat(editingItem.price),
        dietary_labels: editingItem.dietary_labels,
        allergen_info: editingItem.allergen_info,
        image_url: imageUrl
      });
      setItems(items.map(i => i.id === editingItem.id ? res.data : i));
      setEditingItem(null);
      setEditImageFile(null);
      setEditImagePreview(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update item');
    } finally {
      setUploading(false);
    }
  };

  const handleDuplicateItem = async (item) => {
    try {
      const res = await createItem({
        menu_id: menu.id,
        category_id: item.category_id,
        name: item.name + ' (Copy)',
        description: item.description,
        price: item.price,
        dietary_labels: item.dietary_labels,
        allergen_info: item.allergen_info,
        image_url: item.image_url
      });
      setItems([...items, res.data]);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to duplicate item');
    }
  };

  const handleToggle = async (item) => {
    try {
      const res = await toggleItem(item.id, !item.is_available);
      setItems(items.map(i => i.id === item.id ? res.data : i));
    } catch (err) {
      setError('Failed to update availability');
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await deleteItem(id);
      setItems(items.filter(i => i.id !== id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete item');
    }
  };

  const handleItemDragEnd = async (event, catItems) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = catItems.findIndex(i => i.id === active.id);
    const newIndex = catItems.findIndex(i => i.id === over.id);
    const reordered = arrayMove(catItems, oldIndex, newIndex);
    const reorderedIds = reordered.map(i => i.id);
    setItems(prev => {
      const nonCatItems = prev.filter(i => i.category_id !== catItems[0].category_id);
      const updatedCatItems = reorderedIds.map((id, idx) => {
        const item = prev.find(i => i.id === id);
        return { ...item, position: idx + 1 };
      });
      return [...nonCatItems, ...updatedCatItems];
    });
    try {
      await Promise.all(reordered.map((item, idx) => updateItem(item.id, { position: idx + 1 })));
    } catch (err) {
      setError('Failed to save item order');
      fetchAll();
    }
  };

  const toggleDietaryLabel = (label, isEditing) => {
    if (isEditing) {
      const current = editingItem.dietary_labels || [];
      setEditingItem({ ...editingItem, dietary_labels: current.includes(label) ? current.filter(l => l !== label) : [...current, label] });
    } else {
      const current = newItem.dietary_labels;
      setNewItem({ ...newItem, dietary_labels: current.includes(label) ? current.filter(l => l !== label) : [...current, label] });
    }
  };

  if (loading) return <div style={itemStyles.loading}>Loading...</div>;

  return (
    <div style={itemStyles.container}>
      <div style={itemStyles.header}>
        <button style={itemStyles.back} onClick={onBack}>Back to Menus</button>
        <h1 style={itemStyles.headerTitle}>{menu.name}</h1>
      </div>
      <div style={itemStyles.content}>
        {error && <p style={itemStyles.error}>{error}</p>}

        {editingItem && (
          <div style={itemStyles.modal}>
            <div style={itemStyles.modalCard}>
              <h2 style={itemStyles.modalTitle}>Edit Item</h2>
              <form onSubmit={handleEditItem}>
                <input style={itemStyles.input} type="text" placeholder="Item name" value={editingItem.name} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} required />
                <input style={itemStyles.input} type="text" placeholder="Description (optional)" value={editingItem.description || ''} onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })} />
                <input style={itemStyles.input} type="number" placeholder="Price in Naira" value={editingItem.price} onChange={(e) => setEditingItem({ ...editingItem, price: e.target.value })} required />
                <input style={itemStyles.input} type="text" placeholder="Allergen info (optional)" value={editingItem.allergen_info || ''} onChange={(e) => setEditingItem({ ...editingItem, allergen_info: e.target.value })} />
                <div style={itemStyles.labels}>
                  {DIETARY_OPTIONS.map(label => (
                    <button key={label} type="button" style={(editingItem.dietary_labels || []).includes(label) ? itemStyles.labelActive : itemStyles.labelInactive} onClick={() => toggleDietaryLabel(label, true)}>{label}</button>
                  ))}
                </div>
                <div style={itemStyles.imageUpload}>
                  <label style={itemStyles.imageLabel}>Item Image</label>
                  {(editImagePreview || editingItem.image_url) && (
                    <div style={itemStyles.previewWrapper}>
                      <img src={editImagePreview || (editingItem.image_url && editingItem.image_url.startsWith('http') ? editingItem.image_url : (process.env.REACT_APP_API_URL || '') + editingItem.image_url)} alt="Preview" style={itemStyles.preview} />
                      <button type="button" style={itemStyles.removeImage} onClick={() => { setEditImageFile(null); setEditImagePreview(null); setEditingItem({ ...editingItem, image_url: null }); }}>Remove</button>
                    </div>
                  )}
                  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => handleImageChange(e, 'edit')} style={itemStyles.fileInput} />
                </div>
                <div style={itemStyles.modalButtons}>
                  <button style={itemStyles.button} type="submit" disabled={uploading}>{uploading ? 'Saving...' : 'Save Changes'}</button>
                  <button style={itemStyles.cancelBtn} type="button" onClick={() => { setEditingItem(null); setEditImageFile(null); setEditImagePreview(null); }}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {editingCat && (
          <div style={itemStyles.modal}>
            <div style={itemStyles.modalCard}>
              <h2 style={itemStyles.modalTitle}>Rename Category</h2>
              <input style={itemStyles.input} type="text" value={editingCatName} onChange={(e) => setEditingCatName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleRenameCategory()} autoFocus />
              <div style={itemStyles.modalButtons}>
                <button style={itemStyles.button} onClick={handleRenameCategory}>Save</button>
                <button style={itemStyles.cancelBtn} onClick={() => setEditingCat(null)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        <section style={itemStyles.section}>
          <h2 style={itemStyles.sectionTitle}>Categories ({categories.length}/20)</h2>
          <p style={itemStyles.hint}>Drag to reorder.</p>
          <form onSubmit={handleCreateCategory} style={itemStyles.form}>
            <input style={itemStyles.input} type="text" placeholder="New category name" value={newCatName} onChange={(e) => setNewCatName(e.target.value)} />
            <button style={itemStyles.button} type="submit">Add</button>
          </form>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryDragEnd}>
            <SortableContext items={categories.map(c => c.id)} strategy={verticalListSortingStrategy}>
              <div style={itemStyles.tagList}>
                {categories.map(cat => (
                  <SortableCategory key={cat.id} cat={cat} onEdit={(c) => { setEditingCat(c); setEditingCatName(c.name); }} onDelete={handleDeleteCategory} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </section>

        <section style={itemStyles.section}>
          <h2 style={itemStyles.sectionTitle}>Add Item ({items.length}/200)</h2>
          <form onSubmit={handleCreateItem}>
            <select style={itemStyles.input} value={newItem.category_id} onChange={(e) => setNewItem({ ...newItem, category_id: e.target.value })}>
              <option value="">Select category</option>
              {categories.map(cat => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
            </select>
            <input style={itemStyles.input} type="text" placeholder="Item name" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} />
            <input style={itemStyles.input} type="text" placeholder="Description (optional)" value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} />
            <input style={itemStyles.input} type="number" placeholder="Price in Naira" value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: e.target.value })} />
            <input style={itemStyles.input} type="text" placeholder="Allergen info (optional)" value={newItem.allergen_info} onChange={(e) => setNewItem({ ...newItem, allergen_info: e.target.value })} />
            <div style={itemStyles.labels}>
              {DIETARY_OPTIONS.map(label => (
                <button key={label} type="button" style={newItem.dietary_labels.includes(label) ? itemStyles.labelActive : itemStyles.labelInactive} onClick={() => toggleDietaryLabel(label, false)}>{label}</button>
              ))}
            </div>
            <div style={itemStyles.imageUpload}>
              <label style={itemStyles.imageLabel}>Item Image (optional — JPG, PNG or WEBP, max 10MB)</label>
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => handleImageChange(e, 'new')} style={itemStyles.fileInput} />
              {imagePreview && (
                <div style={itemStyles.previewWrapper}>
                  <img src={imagePreview} alt="Preview" style={itemStyles.preview} />
                  <button type="button" style={itemStyles.removeImage} onClick={() => { setImageFile(null); setImagePreview(null); }}>Remove</button>
                </div>
              )}
            </div>
            <button style={uploading ? itemStyles.buttonDisabled : itemStyles.button} type="submit" disabled={uploading}>
              {uploading ? 'Uploading...' : 'Add Item'}
            </button>
          </form>
        </section>

        <section style={itemStyles.section}>
          <h2 style={itemStyles.sectionTitle}>Menu Items</h2>
          <p style={itemStyles.hint}>Drag items within a category to reorder.</p>
          {items.length === 0 ? (
            <p style={itemStyles.muted}>No items yet.</p>
          ) : (
            categories.map(cat => {
              const catItems = items.filter(i => i.category_id === cat.id);
              if (catItems.length === 0) return null;
              return (
                <div key={cat.id} style={itemStyles.categoryGroup}>
                  <h3 style={itemStyles.categoryTitle}>{cat.name}</h3>
                  <SortableCategoryItems
                    categoryId={cat.id}
                    items={items}
                    sensors={sensors}
                    onDragEnd={handleItemDragEnd}
                    onToggle={handleToggle}
                    onEdit={(i) => { setEditingItem(i); setEditImagePreview(null); setEditImageFile(null); }}
                    onDuplicate={handleDuplicateItem}
                    onDelete={handleDeleteItem}
                  />
                </div>
              );
            })
          )}
        </section>
      </div>
    </div>
  );
}

const itemStyles = {
  container: { minHeight: '100vh', backgroundColor: '#f5f5f5' },
  loading: { padding: '40px', textAlign: 'center', color: '#666' },
  header: { backgroundColor: '#c0392b', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: '20px' },
  back: { backgroundColor: 'transparent', border: '1px solid #fff', color: '#fff', padding: '8px 12px', borderRadius: '4px', fontSize: '14px', cursor: 'pointer' },
  headerTitle: { color: '#fff', fontSize: '20px', fontWeight: '700' },
  content: { maxWidth: '800px', margin: '0 auto', padding: '32px 24px' },
  error: { backgroundColor: '#fdecea', color: '#c0392b', padding: '10px', borderRadius: '4px', marginBottom: '16px', fontSize: '14px' },
  section: { backgroundColor: '#fff', padding: '24px', borderRadius: '8px', marginBottom: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  sectionTitle: { fontSize: '16px', fontWeight: '700', marginBottom: '8px', color: '#1a1a1a' },
  hint: { fontSize: '12px', color: '#aaa', marginBottom: '16px' },
  form: { display: 'flex', gap: '12px', marginBottom: '16px' },
  input: { width: '100%', padding: '10px 12px', marginBottom: '10px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px', display: 'block', boxSizing: 'border-box' },
  button: { padding: '10px 20px', backgroundColor: '#c0392b', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap', cursor: 'pointer' },
  buttonDisabled: { padding: '10px 20px', backgroundColor: '#999', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' },
  cancelBtn: { padding: '10px 20px', backgroundColor: '#fff', color: '#666', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', cursor: 'pointer' },
  tagList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  tag: { display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#f5f5f5', padding: '8px 12px', borderRadius: '6px', fontSize: '14px' },
  dragHandle: { cursor: 'grab', color: '#aaa', fontSize: '18px', userSelect: 'none', flexShrink: 0 },
  tagName: { flex: 1 },
  tagEdit: { background: 'none', border: 'none', color: '#666', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' },
  tagDelete: { background: 'none', border: 'none', color: '#c0392b', fontSize: '16px', cursor: 'pointer' },
  labels: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' },
  labelActive: { padding: '6px 12px', backgroundColor: '#c0392b', color: '#fff', border: 'none', borderRadius: '20px', fontSize: '12px', cursor: 'pointer' },
  labelInactive: { padding: '6px 12px', backgroundColor: '#f5f5f5', color: '#444', border: '1px solid #ddd', borderRadius: '20px', fontSize: '12px', cursor: 'pointer' },
  imageUpload: { marginBottom: '12px' },
  imageLabel: { display: 'block', fontSize: '13px', color: '#666', marginBottom: '6px' },
  fileInput: { width: '100%', fontSize: '14px', marginBottom: '8px' },
  previewWrapper: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' },
  preview: { width: '80px', height: '80px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd' },
  removeImage: { background: 'none', border: 'none', color: '#c0392b', fontSize: '13px', cursor: 'pointer' },
  categoryGroup: { marginBottom: '24px' },
  categoryTitle: { fontSize: '14px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
  itemCard: { display: 'flex', alignItems: 'flex-start', padding: '12px', borderRadius: '4px', border: '1px solid #eee', marginBottom: '8px', gap: '8px', backgroundColor: '#fff' },
  itemLeft: { flexShrink: 0 },
  itemImage: { width: '64px', height: '64px', objectFit: 'cover', borderRadius: '4px' },
  itemInfo: { flex: 1 },
  itemName: { fontSize: '15px', fontWeight: '600', color: '#1a1a1a', marginBottom: '2px' },
  itemDesc: { fontSize: '13px', color: '#666', marginBottom: '4px' },
  itemPrice: { fontSize: '14px', fontWeight: '600', color: '#c0392b' },
  labelRow: { display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' },
  labelBadge: { fontSize: '11px', backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '2px 8px', borderRadius: '10px' },
  itemActions: { display: 'flex', flexDirection: 'column', gap: '6px' },
  availableBtn: { padding: '6px 12px', backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #2e7d32', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' },
  unavailableBtn: { padding: '6px 12px', backgroundColor: '#fdecea', color: '#c0392b', border: '1px solid #c0392b', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' },
  editBtn: { padding: '6px 12px', backgroundColor: '#fff', color: '#1a1a1a', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' },
  dupBtn: { padding: '6px 12px', backgroundColor: '#fff', color: '#666', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' },
  deleteButton: { padding: '6px 12px', backgroundColor: '#fff', color: '#c0392b', border: '1px solid #c0392b', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' },
  muted: { color: '#666', fontSize: '14px' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' },
  modalCard: { backgroundColor: '#fff', borderRadius: '8px', padding: '24px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { fontSize: '18px', fontWeight: '700', color: '#1a1a1a', marginBottom: '16px' },
  modalButtons: { display: 'flex', gap: '12px', marginTop: '16px' },
};

export default MenuDetail;
